/**
 * Part Number Detector Utility
 * Detects part numbers in user queries and determines if a query is a direct part number search
 */

export interface PartNumberMatch {
  partNumber: string;
  fullMatch: string;
  confidence: 'high' | 'medium' | 'low';
}

export class PartNumberDetector {
  /**
   * Normalizes vehicle number by removing separators and converting to uppercase
   */
  private static normalizeVehicleNumber(vehicleNumber: string): string {
    // Remove all separators (spaces, hyphens, underscores)
    return vehicleNumber.replace(/[\s\-_]/g, '').toUpperCase();
  }

  /**
   * Validates normalized vehicle number format
   * Must match: 2 letters + 2 digits + 1-2 letters + 4 digits
   */
  private static validateVehicleNumber(normalized: string): boolean {
    const pattern = /^[A-Z]{2}[0-9]{2}[A-Z]{1,2}[0-9]{4}$/;
    return pattern.test(normalized);
  }

  /**
   * Detects Indian vehicle registration number in query
   * Standard format: AA00 AA0000 (e.g., TN99H4459)
   * Bharat Series: 22BH1234AB
   */
  static detectVehicleNumber(query: string): string | null {
    // Standard Indian vehicle number format: AA00 AA0000
    // Flexible regex for detection (allows various separators: spaces, hyphens, underscores)
    const vehiclePattern = /\b[A-Z]{2}[ -_]?[0-9]{2}[ -_]?[A-Z]{1,2}[ -_]?[0-9]{4}\b/i;
    const vehicleMatch = query.match(vehiclePattern);
    if (vehicleMatch) {
      const normalized = this.normalizeVehicleNumber(vehicleMatch[0]);
      if (this.validateVehicleNumber(normalized)) {
        return normalized;
      }
    }
    
    // Bharat Series format: 22BH1234AB
    const bharatPattern = /\b[0-9]{2}BH[0-9]{4}[A-Z]{1,2}\b/i;
    const bharatMatch = query.match(bharatPattern);
    if (bharatMatch) {
      return bharatMatch[0].toUpperCase();
    }
    
    return null;
  }

  /**
   * Detects part numbers in a query string, excluding vehicle numbers
   * Supports various patterns:
   * - Direct alphanumeric codes (3-20 chars, may include hyphens/underscores)
   * - "part number" / "part no" / "pn" phrases followed by code
   * - Standalone alphanumeric patterns in query
   */
  static detectPartNumber(query: string): PartNumberMatch | null {
    if (!query || query.trim().length === 0) {
      return null;
    }

    const trimmedQuery = query.trim();

    // Detect vehicle number so we can exclude it from part number matching
    const vehicleNumber = this.detectVehicleNumber(trimmedQuery);

    // If the ENTIRE query is just a vehicle number, it's not a part number search
    if (vehicleNumber && trimmedQuery.replace(/[\s\-_]/g, '').toUpperCase() === vehicleNumber) {
      return null;
    }

    // Remove vehicle number from query to avoid matching it as a part number
    let queryForPartDetection = trimmedQuery;
    if (vehicleNumber) {
      // Remove the vehicle number portion from the query for part number detection
      const vehiclePattern = /\b[A-Z]{2}[ -_]?[0-9]{2}[ -_]?[A-Z]{1,2}[ -_]?[0-9]{4}\b/i;
      const bharatPattern = /\b[0-9]{2}BH[0-9]{4}[A-Z]{1,2}\b/i;
      queryForPartDetection = trimmedQuery.replace(vehiclePattern, ' ').replace(bharatPattern, ' ').trim();
    }

    // Pattern 1: "part number", "part no", "pn" followed by alphanumeric code
    const partNumberPhrasePattern = /(?:part\s*(?:number|no)|pn|part#)\s*[:\-]?\s*([A-Z0-9\-_]{3,20})/i;
    const phraseMatch = queryForPartDetection.match(partNumberPhrasePattern);
    if (phraseMatch && phraseMatch[1]) {
      const extracted = phraseMatch[1].toUpperCase();
      // Double-check it's not a vehicle number
      if (this.detectVehicleNumber(extracted)) {
        return null; // It's a vehicle number
      }
      return {
        partNumber: extracted,
        fullMatch: phraseMatch[0],
        confidence: 'high'
      };
    }

    // Pattern 2: Direct alphanumeric code (standalone or at start/end of query)
    // Must be 3-20 chars, contain at least one letter and one number, may include hyphens/underscores
    const alphanumericPattern = /\b([A-Z]{1,5}[-_]?[0-9]{2,15}|[0-9]{2,10}[-_]?[A-Z]{1,5}|[A-Z0-9\-_]{5,20})\b/gi;
    const matches = queryForPartDetection.match(alphanumericPattern);

    if (matches) {
      for (const match of matches) {
        // Check if this match is a vehicle number (exclude it)
        if (this.detectVehicleNumber(match)) {
          continue; // Skip vehicle numbers
        }

        const cleanedMatch = match.replace(/[-_]/g, '');

        // Must have both letters and numbers
        const hasLetters = /[A-Z]/i.test(cleanedMatch);
        const hasNumbers = /[0-9]/.test(cleanedMatch);

        if (hasLetters && hasNumbers && cleanedMatch.length >= 3 && cleanedMatch.length <= 20) {
          // Check if it's not a common word or vehicle model
          const commonWords = ['SUV', 'ABS', 'GPS', 'USB', 'LED', 'AC', 'PC', 'TV'];
          const isCommonWord = commonWords.includes(match.toUpperCase());

          if (!isCommonWord) {
            // If the match is at start or end of query and query is short, higher confidence
            const isAtStart = trimmedQuery.toUpperCase().startsWith(match.toUpperCase());
            const isAtEnd = trimmedQuery.toUpperCase().endsWith(match.toUpperCase());
            const isShortQuery = trimmedQuery.length <= 30;

            const confidence = (isAtStart || isAtEnd) && isShortQuery ? 'high' : 'medium';

            return {
              partNumber: match.toUpperCase(),
              fullMatch: match,
              confidence
            };
          }
        }
      }
    }

    return null;
  }

  /**
   * Checks if a query is likely just a part number (direct part number search)
   * Returns true if query appears to be primarily a part number with minimal other text
   */
  static isDirectPartNumber(query: string): boolean {
    if (!query || query.trim().length === 0) {
      return false;
    }

    const trimmedQuery = query.trim();
    const match = this.detectPartNumber(trimmedQuery);

    if (!match) {
      return false;
    }

    // If the part number takes up most of the query, it's likely a direct search
    const partNumberRatio = match.fullMatch.length / trimmedQuery.length;
    
    // Also check if query is short and mostly contains the part number
    if (partNumberRatio >= 0.7 || trimmedQuery.length <= 15) {
      return true;
    }

    // Check if query is just the part number with minimal surrounding text
    const surroundingText = trimmedQuery.replace(new RegExp(match.fullMatch, 'gi'), '').trim();
    return surroundingText.length <= 5; // Allow small words like "for", "the", etc.
  }

  /**
   * Checks if a query contains a part number (not necessarily direct)
   */
  static containsPartNumber(query: string): boolean {
    return this.detectPartNumber(query) !== null;
  }
}

